/**
 * Backup-camera-style steering guide for the driver's **requested input**.
 *
 * Projects the wheel track onto the video as two curved rails that bend with
 * the steering angle. Only the local input preview lives here: the
 * authoritative visualization - the amber rails, distance rungs, and gear tag
 * for the command the truck actually **accepted** - is rendered truck-side and
 * burned into the video itself (see `truck/src/bin/sim.rs`), so the player
 * never fabricates simulation state.
 *
 * @module
 */
import type { DriveState } from "./schemas.ts";

export interface GuideStyle {
	/** Stroke color (CSS). */
	color: string;
	/** Line width in CSS pixels. */
	width: number;
	/** Dash pattern, or empty for solid. */
	dash?: number[];
	/** Overall opacity. */
	alpha?: number;
}

// How far the far end of the rails swings sideways at full lock, as a fraction
// of width. Tuned to read like a reversing camera, not a literal kinematic model.
// Keep in sync with the truck-side renderer (sim.rs) so cyan overlays amber.
const CURVE_GAIN = 0.34;

// Vertical extent of the projected ground plane (fractions of canvas height).
const Y_NEAR = 0.98;
const Y_FAR = 0.46;
// Half-width of the wheel track at the near and far ends (fractions of width).
const HALF_NEAR = 0.36;
const HALF_FAR = 0.06;

const SEGMENTS = 48;

/** One rail point. */
interface Pt {
	x: number;
	y: number;
}

/** Sample the left and right rails for a given steering angle. */
function rails(w: number, h: number, steering: number): { left: Pt[]; right: Pt[] } {
	const cx = w / 2;
	const left: Pt[] = [];
	const right: Pt[] = [];
	for (let i = 0; i < SEGMENTS; i++) {
		const t = i / (SEGMENTS - 1);
		// Perspective: rails converge and rise toward the horizon.
		const y = (Y_NEAR + (Y_FAR - Y_NEAR) * t) * h;
		const half = (HALF_NEAR + (HALF_FAR - HALF_NEAR) * t) * w;
		// Lateral swing grows with distance (t²) so the rails curve, not shear.
		const shift = steering * CURVE_GAIN * t * t * w;
		left.push({ x: cx - half + shift, y });
		right.push({ x: cx + half + shift, y });
	}
	return { left, right };
}

function stroke(ctx: CanvasRenderingContext2D, pts: Pt[]) {
	ctx.beginPath();
	pts.forEach((p, i) => {
		if (i) ctx.lineTo(p.x, p.y);
		else ctx.moveTo(p.x, p.y);
	});
	ctx.stroke();
}

/** Draw one trajectory (two rails). */
export function drawGuide(ctx: CanvasRenderingContext2D, w: number, h: number, drive: DriveState, style: GuideStyle) {
	const { left, right } = rails(w, h, drive.steering);

	ctx.save();
	ctx.globalAlpha = style.alpha ?? 1;
	ctx.lineJoin = "round";
	ctx.lineCap = "round";
	ctx.lineWidth = style.width;
	ctx.strokeStyle = style.color;
	ctx.setLineDash(style.dash ?? []);
	stroke(ctx, left);
	stroke(ctx, right);
	ctx.restore();
}
